import {
  ConversationEventType,
  ConversationMetadata,
  ConversationStatus,
  ConversationSummary,
  ConversationType,
  PhoneConversationMetadata,
} from '@shared/generated/graphql';
import { ChatViewContext } from 'clerk_common';
import { InteractiveCard } from 'clerk_common/cards/types';
import { isNil } from 'lodash';
import { ConversationAnalysis } from './conversationAnalysis';
import { ConversationRating } from './rating';

export type Conversation = {
  id: string;
  title?: string;
  externalIdentifier: string;
  createdAt: Date;
  type: ConversationType;
  status: ConversationStatus;
  user?: {
    id: string;
    email?: string;
    firstName?: string;
    lastName?: string;
  };
  summary?: ConversationSummary;
  metadata?: ConversationMetadata;
  mediaUrl?: string;
  organizationId: string;
  ratings?: ConversationRating[];
  analyses?: ConversationAnalysis[];
  events?: ConversationEvent[];
};

export enum ConversationContributionRole {
  USER = 'user',
  ASSISTANT = 'assistant',
  SYSTEM = 'system',
}

export enum ConversationMessageEventType {
  VOICE_CONTRIBUTION = 'VOICE_CONTRIBUTION',
  EMAIL_CONTRIBUTION = 'EMAIL_CONTRIBUTION',
  SYSTEM_MESSAGE = 'SYSTEM_MESSAGE',
  CHAT_MESSAGE = 'CHAT_MESSAGE',
  UNKNOWN = 'UNKNOWN',
}

export type MessageAttachment = {
  id: string;
  name?: string;
  url: string;
  metadata?: Record<string, any>;
};

export type VoiceContribution = {
  id: string;
  type: ConversationMessageEventType.VOICE_CONTRIBUTION;
  createdAt: Date;
  role: ConversationContributionRole;
  transcript: string;
  conversationId?: string;
};

export type EmailContribution = {
  id: string;
  type: ConversationMessageEventType.EMAIL_CONTRIBUTION;
  createdAt: Date;
  sanitizedHtmlBody: string;
  subject: string;
  sender: string;
  toRecipients: string[];
  emailReceivedAt?: Date;
  role?: ConversationContributionRole;
  conversationId?: string;
};

export type SystemMessageEvent = {
  id: string;
  type: ConversationMessageEventType.SYSTEM_MESSAGE;
  createdAt: Date;
  message: string;
  conversationId?: string;
};

export enum ExternalPlatform {
  SMS = 'SMS',
  SLACK = 'SLACK',
  MICROSOFT_TEAMS = 'MICROSOFT_TEAMS',
  ASSISTANT_EMAIL = 'ASSISTANT_EMAIL',
}

export type MessageSender = {
  id: string;
  name?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
};

export type ChatMessageEvent = {
  id: string;
  type: ConversationMessageEventType.CHAT_MESSAGE;
  createdAt: Date;
  role: ConversationContributionRole;
  message: string;
  externalPlatform?: ExternalPlatform;
  conversationId?: string;
  canonicalMessageId?: string;
  attachments?: MessageAttachment[];
  sender?: MessageSender;
  card?: InteractiveCard;
};

export type UnknownEvent = {
  id: string;
  type: ConversationMessageEventType.UNKNOWN;
  createdAt: Date;
  conversationId?: string;
};

export type CallFailedEventData = {
  eventDataType: ConversationEventType.CALL_FAILED;
};

export type CallEndedEventData = {
  eventDataType: ConversationEventType.CALL_ENDED;
};

export type ChatViewContextUpdatedEventData = {
  eventDataType: ConversationEventType.CHAT_VIEW_CONTEXT_UPDATED;
  context: ChatViewContext;
};

export type CallTransferredEventData = {
  eventDataType: ConversationEventType.CALL_TRANSFERRED;
  phoneNumber: string;
  extension?: string | null;
  overrideCallerId?: boolean | null;
};

export type CallTransferAcceptedEventData = {
  eventDataType: ConversationEventType.CALL_TRANSFER_ACCEPTED;
  conferenceId: string;
  transferCallSid: string;
};

export type ConferenceJoinedEventData = {
  eventDataType: ConversationEventType.CONFERENCE_JOINED;
  conferenceId: string;
};

export type AsyncTransferEventData = {
  eventDataType: ConversationEventType.ASYNC_TRANSFER;
  ccRecipients: string[];
  shipmentId?: string | null;
};

export type ReturnedToTransferredCallEventData = {
  eventDataType: ConversationEventType.RETURNED_TO_TRANSFERRED_CALL;
  transferAttemptConversationId?: string | null;
  conferenceId: string;
};

export type CallRecordingStartedEventData = {
  eventDataType: ConversationEventType.CALL_RECORDING_STARTED;
};

export type CallRecordingReceivedEventData = {
  eventDataType: ConversationEventType.CALL_RECORDING_RECEIVED;
  recordingStartTime: Date;
  recordingEndTime: Date;
};

export type WatcherClosedEventData = {
  eventDataType: ConversationEventType.CONVERSATION_CLOSED_BY_WATCHER;
};

export type UnknownConversationEventData = {
  eventDataType: ConversationEventType.UNKNOWN;
};

export const QUIET_LINE_INTENT_EVENT_TYPES = [
  ConversationEventType.INTENT_TO_HANG_UP_REGISTERED,
  ConversationEventType.INTENT_TO_HANG_UP_CANCELLED,
  ConversationEventType.INTENT_TO_TRANSFER_REGISTERED,
  ConversationEventType.INTENT_TO_TRANSFER_CANCELLED,
  ConversationEventType.INTENT_TO_HOLD_REGISTERED,
  ConversationEventType.INTENT_TO_HOLD_CANCELLED,
] as const;

export type QuietLineIntentEventType =
  (typeof QUIET_LINE_INTENT_EVENT_TYPES)[number];

export function isQuietLineIntentEventType(
  eventType: ConversationEventType
): eventType is QuietLineIntentEventType {
  return (QUIET_LINE_INTENT_EVENT_TYPES as readonly string[]).includes(
    eventType
  );
}

export type QuietLineIntentEventData = {
  eventDataType: QuietLineIntentEventType;
};

export type ConversationMessageEvent =
  | VoiceContribution
  | EmailContribution
  | SystemMessageEvent
  | UnknownEvent
  | ChatMessageEvent;

export type ConversationEventData =
  | CallFailedEventData
  | CallEndedEventData
  | ChatViewContextUpdatedEventData
  | CallTransferredEventData
  | CallTransferAcceptedEventData
  | ConferenceJoinedEventData
  | AsyncTransferEventData
  | ReturnedToTransferredCallEventData
  | CallRecordingStartedEventData
  | CallRecordingReceivedEventData
  | WatcherClosedEventData
  | QuietLineIntentEventData
  | UnknownConversationEventData;

export type ConversationEvent = {
  id: string;
  createdAt: Date;
  name: ConversationEventType;
  data?: ConversationEventData;
};

export type ConversationLifecycleEvent = ConversationEvent & {
  conversationId: string;
};

export function isPhoneConversationMetadata(
  metadata?: ConversationMetadata
): metadata is PhoneConversationMetadata {
  return (
    !isNil(metadata) &&
    'fromPhoneNumber' in metadata &&
    'toPhoneNumber' in metadata
  );
}

export type ExternalMessagePreview = {
  id: string;
  externalIdentifier: string;
  createdAt: Date;
  userId?: string | null;
};

export type ConversationWithExternalMessages = Conversation & {
  externalMessages: ExternalMessagePreview[];
};
